import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CRow, CCol,
  CCard, CCardBody, CCardHeader,
  CTable, CTableHead, CTableRow, CTableHeaderCell,
  CTableBody, CTableDataCell,
  CSpinner, CAlert, CBadge,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPeople, cilTask, cilShieldAlt, cilChatBubble, cilNotes, cilSend, cilGift } from '@coreui/icons';
import { isFinance, isAdmin, getUser } from '../services/auth';
import api from '../services/api';
import { useNotifications } from '../context/NotificationContext';

const STATUS_COLOR = {
  'Baru':                   'primary',
  'Dalam Proses':           'warning',
  'Selesai':                'success',
  'Dipulangkan':            'info',
  'Menunggu Maklumbalas':   'secondary',
  'Waiting to Pickup':      'dark',
};

function StatCard({ label, value, icon, color, onClick }) {
  return (
    <CCard
      className="border-0 shadow-sm h-100"
      style={{ borderRadius: 12, overflow: 'hidden', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <CCardBody className="d-flex align-items-center justify-content-between p-4" style={{ background: color }}>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 6 }}>
            {label}
          </div>
          <div style={{ color: '#fff', fontSize: 32, lineHeight: 1 }}>
            {value ?? 0}
          </div>
        </div>
        <CIcon icon={icon} style={{ width: 52, height: 52, color: 'rgba(255,255,255,0.3)' }} />
      </CCardBody>
    </CCard>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { onlineStaff: liveOnlineStaff } = useNotifications();

  useEffect(() => {
    api.get('/dashboard')
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load dashboard data.'));
  }, []);

  if (error) return <CAlert color="danger">{error}</CAlert>;
  if (!data) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
      <CSpinner color="success" />
    </div>
  );

  return (
    <>
      {/* Stat cards */}
      <CRow className="mb-4 g-3">
        <CCol xs={12} sm={4}>
          <StatCard label="Customers" value={data.totalCustomers} icon={cilPeople} color="#4e73df"
            onClick={() => navigate('/customers')} />
        </CCol>
        <CCol xs={12} sm={4}>
          <StatCard label="Total Jobs" value={data.totalJobs} icon={cilTask} color="#1cc88a"
            onClick={() => navigate('/jobs')} />
        </CCol>
        <CCol xs={12} sm={4}>
          <StatCard label="Warranty Claims" value={data.totalWarranty} icon={cilShieldAlt} color="#f6c23e"
            onClick={() => navigate('/warranty')} />
        </CCol>
        {data.smsEnabled && (
          <CCol xs={12} sm={4}>
            <StatCard label="SMS Credits" value={data.smsBalance ?? '—'} icon={cilChatBubble} color="#858796" />
          </CCol>
        )}
        {/* Online staff — prefer live WS list, fall back to initial API snapshot */}
        {(() => {
          const staff = liveOnlineStaff.length > 0 ? liveOnlineStaff : (data.onlineStaff || []);
          return (
            <CCol xs={12} sm={4}>
              <CCard
                className="border-0 shadow-sm h-100"
                style={{ borderRadius: 12, overflow: 'hidden', cursor: isAdmin() ? 'pointer' : 'default' }}
                onClick={() => isAdmin() && navigate('/admin/users')}
              >
                <CCardBody className="p-4" style={{ background: '#2ecc71' }}>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 8 }}>
                    Online Staff
                  </div>
                  {staff.length === 0 ? (
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>No one online</div>
                  ) : isAdmin() ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {staff.map(s => (
                        <span key={s.username} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '3px 10px' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', flexShrink: 0 }} />
                          <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{s.username}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#fff', fontSize: 32, lineHeight: 1 }}>{staff.length}</div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>
          );
        })()}
      </CRow>

      {data.marketing && !data.marketing.unavailable && (
        <CRow className="mb-4 g-3">
          <CCol xs={12}>
            <h6 className="text-muted text-uppercase mb-2" style={{ letterSpacing: '0.06em', fontSize: 12 }}>Marketing (rolling 3 months)</h6>
          </CCol>
          <CCol xs={12} sm={4}>
            <StatCard
              label="Messages marked sent"
              value={data.marketing.sentLast3Months ?? 0}
              icon={cilSend}
              color="#128C7E"
              onClick={() => navigate('/marketing/send')}
            />
          </CCol>
          <CCol xs={12} sm={4}>
            <StatCard
              label="Eligible customers (now)"
              value={data.marketing.eligibleCount ?? 0}
              icon={cilPeople}
              color="#20c997"
              onClick={() => navigate('/marketing/send')}
            />
          </CCol>
          <CCol xs={12} sm={4}>
            <StatCard
              label="Sent vs eligible %"
              value={data.marketing.reachPct != null ? `${data.marketing.reachPct}%` : '—'}
              icon={cilNotes}
              color="#2c9faf"
              onClick={() => navigate('/marketing/templates')}
            />
          </CCol>
          {(data.marketing.leaderboard || []).length > 0 && (
            <CCol xs={12}>
              <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
                <CCardHeader className="bg-white border-bottom px-4 py-3" style={{ borderRadius: '12px 12px 0 0' }}>
                  Marketing leaderboard (marked sent, last 3 months)
                </CCardHeader>
                <CCardBody className="px-4 py-3">
                  <CTable responsive className="mb-0 small">
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell scope="col">#</CTableHeaderCell>
                        <CTableHeaderCell scope="col">Staff</CTableHeaderCell>
                        <CTableHeaderCell scope="col" className="text-end">Messages</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {data.marketing.leaderboard.map((row, idx) => {
                        const me = getUser()?.username;
                        const isMe = me && row.username === me;
                        return (
                          <CTableRow key={row.username || idx}>
                            <CTableDataCell>{idx + 1}</CTableDataCell>
                            <CTableDataCell>
                              {row.username || '—'}
                              {isMe && <CBadge color="success" className="ms-2">You</CBadge>}
                            </CTableDataCell>
                            <CTableDataCell className="text-end fw-semibold">{row.count}</CTableDataCell>
                          </CTableRow>
                        );
                      })}
                    </CTableBody>
                  </CTable>
                </CCardBody>
              </CCard>
            </CCol>
          )}
        </CRow>
      )}

      {isFinance() && data.finance && (
        <CRow className="mb-4 g-3">
          <CCol xs={12}>
            <h6 className="text-muted text-uppercase mb-2" style={{ letterSpacing: '0.06em', fontSize: 12 }}>Finance</h6>
          </CCol>
          {data.finance.tablesMissing && (
            <CCol xs={12}>
              <CAlert color="warning" className="mb-0 py-2">
                Finance tables are not available yet. Restart the API after deployment so invoices and payments can be created.
              </CAlert>
            </CCol>
          )}
          <CCol xs={12} sm={4}>
            <StatCard
              label="Total invoiced"
              value={`RM ${Number(data.finance.totalInvoiced || 0).toFixed(2)}`}
              icon={cilNotes}
              color="#2c9faf"
              onClick={() => navigate('/finance/invoices')}
            />
          </CCol>
          <CCol xs={12} sm={4}>
            <StatCard
              label="Collected"
              value={`RM ${Number(data.finance.totalPaid || 0).toFixed(2)}`}
              icon={cilTask}
              color="#1cc88a"
              onClick={() => navigate('/finance/payments')}
            />
          </CCol>
          <CCol xs={12} sm={4}>
            <StatCard
              label="Pending payment (AR)"
              value={`RM ${Number(data.finance.outstanding || 0).toFixed(2)}`}
              icon={cilNotes}
              color="#e74a3b"
              onClick={() => navigate('/finance/invoices')}
            />
          </CCol>

          {/* FMZ Points summary */}
          {data.finance.pointsSummary && (
            <>
              <CCol xs={12} sm={6}>
                <StatCard
                  label="FMZ Points issued"
                  value={data.finance.pointsSummary.totalCreated}
                  icon={cilGift}
                  color="#f6a623"
                  onClick={() => navigate('/finance/points-history')}
                />
              </CCol>
              <CCol xs={12} sm={6}>
                <StatCard
                  label="FMZ Points redeemed"
                  value={data.finance.pointsSummary.totalSpent}
                  icon={cilGift}
                  color="#9b59b6"
                  onClick={() => navigate('/finance/points-history')}
                />
              </CCol>
              {(data.finance.pointsSummary.recentTxns || []).length > 0 && (
                <CCol xs={12}>
                  <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
                    <CCardHeader className="bg-white border-bottom px-4 py-3 d-flex align-items-center justify-content-between" style={{ borderRadius: '12px 12px 0 0' }}>
                      <span><CIcon icon={cilGift} className="me-2 text-warning" />Recent FMZ Points activity</span>
                      <span className="small text-primary" style={{ cursor: 'pointer' }} onClick={() => navigate('/finance/points-history')}>View all →</span>
                    </CCardHeader>
                    <CCardBody className="px-4 py-3">
                      <CTable responsive className="mb-0 small">
                        <CTableHead>
                          <CTableRow>
                            <CTableHeaderCell>Customer</CTableHeaderCell>
                            <CTableHeaderCell>Type</CTableHeaderCell>
                            <CTableHeaderCell>Note</CTableHeaderCell>
                            <CTableHeaderCell className="text-end">Points</CTableHeaderCell>
                            <CTableHeaderCell>Date</CTableHeaderCell>
                          </CTableRow>
                        </CTableHead>
                        <CTableBody>
                          {data.finance.pointsSummary.recentTxns.map((t) => (
                            <CTableRow key={t.id}>
                              <CTableDataCell>{t.customer_name || t.customer_uid}</CTableDataCell>
                              <CTableDataCell>
                                <CBadge color={t.points > 0 ? 'success' : 'danger'}>
                                  {t.type === 'job_earn' ? 'Job Earn' : t.type === 'points_spend' ? 'Redeemed' : t.type === 'reg_bonus' ? 'Reg Bonus' : t.type}
                                </CBadge>
                              </CTableDataCell>
                              <CTableDataCell className="text-muted">{t.note || '—'}</CTableDataCell>
                              <CTableDataCell className={`text-end fw-semibold ${t.points > 0 ? 'text-success' : 'text-danger'}`}>
                                {t.points > 0 ? '+' : ''}{t.points}
                              </CTableDataCell>
                              <CTableDataCell>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</CTableDataCell>
                            </CTableRow>
                          ))}
                        </CTableBody>
                      </CTable>
                    </CCardBody>
                  </CCard>
                </CCol>
              )}
            </>
          )}

          {(data.finance.recentPayments || []).length > 0 && (
            <CCol xs={12}>
              <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
                <CCardHeader className="bg-white border-bottom px-4 py-3" style={{ borderRadius: '12px 12px 0 0' }}>
                  Recent payments
                </CCardHeader>
                <CCardBody className="px-4 py-3">
                  <CTable responsive className="mb-0 small">
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell scope="col">Receipt</CTableHeaderCell>
                        <CTableHeaderCell scope="col">Invoice</CTableHeaderCell>
                        <CTableHeaderCell scope="col">Job</CTableHeaderCell>
                        <CTableHeaderCell scope="col" className="text-end">Amount</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {data.finance.recentPayments.map((p) => (
                        <CTableRow key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/finance/payments/${p.id}/receipt`)}>
                          <CTableDataCell>PAY-{p.id}</CTableDataCell>
                          <CTableDataCell>{p.invoice_no}</CTableDataCell>
                          <CTableDataCell>{p.jobcode}</CTableDataCell>
                          <CTableDataCell className="text-end">RM {Number(p.amount).toFixed(2)}</CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </CCardBody>
              </CCard>
            </CCol>
          )}
        </CRow>
      )}

      {/* Jobs by Status */}
      <CRow className="mb-4 g-3">
        <CCol xs={12}>
          <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
            <CCardHeader className="bg-white border-bottom px-4 py-3" style={{ borderRadius: '12px 12px 0 0' }}>
              Jobs by Status
            </CCardHeader>
            <CCardBody className="px-4 py-3">
              <CRow className="g-3">
                {Object.entries(data.statusCounts || {}).map(([status, count]) => (
                  <CCol xs={6} md={4} lg={3} key={status}>
                    <div
                      className="border rounded p-3 d-flex flex-column gap-1"
                      style={{ borderRadius: 8, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                      onClick={() => navigate(`/jobs?status=${encodeURIComponent(status)}`)}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.10)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
                    >
                      <CBadge color={STATUS_COLOR[status] || 'secondary'} style={{ width: 'fit-content' }}>
                        {status}
                      </CBadge>
                      <span style={{ fontSize: 24 }}>{count}</span>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* My Jobs by Status — non-admin only */}
      {!isAdmin() && data.myStatusCounts && Object.keys(data.myStatusCounts).length > 0 && (
        <CRow className="mb-4 g-3">
          <CCol xs={12}>
            <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
              <CCardHeader className="bg-white border-bottom px-4 py-3" style={{ borderRadius: '12px 12px 0 0' }}>
                My Jobs by Status
              </CCardHeader>
              <CCardBody className="px-4 py-3">
                <CRow className="g-3">
                  {Object.entries(data.myStatusCounts).map(([status, count]) => (
                    <CCol xs={6} md={4} lg={3} key={status}>
                      <div
                        className="border rounded p-3 d-flex flex-column gap-1"
                        style={{ borderRadius: 8, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                        onClick={() => navigate(`/jobs?status=${encodeURIComponent(status)}&admin_id=${encodeURIComponent(getUser()?.username || '')}&label=${encodeURIComponent(`My jobs · ${status}`)}`)}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.10)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
                      >
                        <CBadge color={STATUS_COLOR[status] || 'secondary'} style={{ width: 'fit-content' }}>
                          {status}
                        </CBadge>
                        <span style={{ fontSize: 24 }}>{count}</span>
                      </div>
                    </CCol>
                  ))}
                </CRow>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      {/* Recent Jobs */}
      <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
        <CCardHeader className="bg-white border-bottom px-4 py-3" style={{ borderRadius: '12px 12px 0 0' }}>
          Recent Jobs
        </CCardHeader>
        <CCardBody className="p-0">
          <CTable hover responsive className="mb-0" style={{ fontSize: 14 }}>
            <CTableHead color="light">
              <CTableRow>
                <CTableHeaderCell>Job Code</CTableHeaderCell>
                <CTableHeaderCell>Customer ID</CTableHeaderCell>
                <CTableHeaderCell>Status</CTableHeaderCell>
                <CTableHeaderCell>Date</CTableHeaderCell>
                {isAdmin() && <CTableHeaderCell>Staff</CTableHeaderCell>}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {(data.recentJobs || []).map(job => (
                <CTableRow
                  key={job.jid}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/jobs/${job.jobcode}/edit`)}
                >
                  <CTableDataCell>{job.jobcode}</CTableDataCell>
                  <CTableDataCell>{job.custid}</CTableDataCell>
                  <CTableDataCell>
                    <CBadge color={STATUS_COLOR[job.status] || 'secondary'}>
                      {job.status}
                    </CBadge>
                  </CTableDataCell>
                  <CTableDataCell>{job.date}</CTableDataCell>
                  {isAdmin() && <CTableDataCell>{job.admin_id}</CTableDataCell>}
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </CCardBody>
      </CCard>
    </>
  );
}
